import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client.ts';
import { X, UploadCloud, Download, CheckCircle2, AlertCircle, Loader2, RefreshCw, FileSpreadsheet, Info, Filter } from 'lucide-react';

interface BulkEvaluateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface RowLogItem {
  rowNum: number;
  ticketId: string;
  ideaName: string;
  evaluationCategory?: string;
  assigneeEmail?: string;
  comments?: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  reason: string;
}

interface ImportSummaryData {
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  rowLogs: RowLogItem[];
}

export function BulkEvaluateImportModal({ isOpen, onClose, onSuccess }: BulkEvaluateImportModalProps) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importResult, setImportResult] = useState<ImportSummaryData | null>(null);
  const [logFilter, setLogFilter] = useState<'ALL' | 'SUCCESS' | 'FAILED' | 'SKIPPED'>('ALL');

  if (!isOpen) return null;

  const handleDownloadSample = () => {
    const sampleData = [
      {
        'Ticket ID': 'CG1002345',
        'Idea Name': 'Optimize SAP Batch Process',
        'Idea Evaluation Category': 'A,C',
        'Assignee Email': 'committee.member1@company.com, committee.member2@company.com',
        'Comments': 'High potential savings; priority evaluation',
      },
      {
        'Ticket ID': 'CG1002346',
        'Idea Name': 'Reduce Material Scrap Rate',
        'Idea Evaluation Category': 'B',
        'Assignee Email': 'committee.member3@company.com',
        'Comments': 'Assigned for detailed feasibility review',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample Update Data');
    XLSX.writeFile(workbook, 'Idea_Evaluation_Import_Sample.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setErrorMessage('');
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file) {
      setErrorMessage('Please select an Excel file (.xlsx, .xls, or .csv) to import.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];

      const rawRows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rawRows.length === 0) {
        setErrorMessage('The selected Excel file contains no data rows.');
        setIsProcessing(false);
        return;
      }

      // Map Excel column names dynamically (flexible header matching)
      const parsedRows = rawRows.map((r: any, idx: number) => {
        const findVal = (possibleKeys: string[]) => {
          for (const key of Object.keys(r)) {
            const cleanKey = key.trim().toLowerCase();
            if (possibleKeys.some(p => cleanKey.includes(p.toLowerCase()))) {
              return r[key];
            }
          }
          return '';
        };

        const ticketId = findVal(['ticket id', 'ticketid', 'ticket_id', 'ticket']);
        const ideaName = findVal(['idea name', 'ideaname', 'idea_name', 'name of idea', 'title']);
        const evalCat = findVal(['idea evaluation category', 'evaluation category', 'eval category', 'category']);
        const assigneeEmail = findVal(['assignee email', 'assignee_email', 'assignee', 'user email', 'email']);
        const comments = findVal(['comments', 'comment', 'remarks', 'note']);

        return {
          rowNum: idx + 2, // Excel row # (1-indexed + header)
          ticketId: String(ticketId).trim(),
          ideaName: String(ideaName).trim(),
          evaluationCategory: String(evalCat).trim(),
          assigneeEmail: String(assigneeEmail).trim(),
          comments: String(comments).trim(),
        };
      });

      const res = await apiClient.post('/admin/ideas/bulk-evaluate-import', {
        rows: parsedRows,
      });

      if (res.data.success) {
        setImportResult(res.data.data);
        queryClient.invalidateQueries({ queryKey: ['leaderIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminStats'] });
        if (onSuccess) onSuccess();
      } else {
        setErrorMessage(res.data.error?.message || 'Failed to process import.');
      }
    } catch (err: any) {
      console.error('Import Error:', err);
      setErrorMessage(err.response?.data?.error?.message || err.message || 'Error processing Excel file.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setImportResult(null);
    setErrorMessage('');
    setLogFilter('ALL');
  };

  const filteredLogs = importResult?.rowLogs.filter((log) => {
    if (logFilter === 'SUCCESS') return log.status === 'SUCCESS';
    if (logFilter === 'FAILED') return log.status === 'FAILED';
    if (logFilter === 'SKIPPED') return log.status === 'SKIPPED';
    return true;
  }) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Import Idea Evaluations & Assignees</h3>
              <p className="text-xs text-slate-500">Bulk update categories, assignees, and comments via Excel</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {!importResult ? (
            /* Upload View */
            <div className="space-y-6">
              {/* Instructions & Template Download */}
              <div className="bg-blue-50/70 border border-blue-200/80 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 text-blue-900 font-semibold text-xs">
                    <Info className="w-4 h-4 text-blue-600" />
                    <span>Excel Format Requirements:</span>
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    Required fields: <strong className="font-semibold">Ticket ID</strong> and <strong className="font-semibold">Idea Name</strong> (both must match existing idea).<br />
                    Supports multiple categories (e.g. <code className="bg-blue-100 px-1 rounded">A,C</code>) and multiple assignee emails (e.g. <code className="bg-blue-100 px-1 rounded">e1@cg.com, e2@cg.com</code>).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="px-3 py-2 bg-white border border-blue-300 hover:bg-blue-50 text-blue-700 font-medium text-xs rounded-lg transition-colors flex items-center space-x-1.5 shrink-0 shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Sample Template</span>
                </button>
              </div>

              {/* Error Alert */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* File Dropzone / Picker */}
              <div className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-2xl p-8 text-center bg-slate-50/50 hover:bg-emerald-50/30 transition-all group cursor-pointer relative">
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                />
                <div className="flex flex-col items-center justify-center space-y-3 pointer-events-none">
                  <div className="p-3 bg-white shadow-sm border border-slate-200 rounded-2xl group-hover:scale-110 group-hover:border-emerald-300 transition-all">
                    <UploadCloud className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      {file ? file.name : 'Click to upload or drag and drop Excel file'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Supports .XLSX, .XLS, or .CSV formats</p>
                  </div>
                </div>
              </div>

              {file && (
                <div className="flex items-center justify-between p-3 bg-emerald-50/80 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                  <span className="font-semibold truncate">Selected File: {file.name}</span>
                  <span className="text-slate-500">{(file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>
          ) : (
            /* Upload Summary Logs View */
            <div className="space-y-6">
              {/* Summary KPI Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Rows</p>
                  <p className="text-2xl font-bold text-slate-800 mt-0.5">{importResult.totalCount}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Updated</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-0.5">{importResult.successCount}</p>
                </div>
                <div className="bg-red-50 border border-red-200 p-3.5 rounded-xl">
                  <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider">Failed</p>
                  <p className="text-2xl font-bold text-red-700 mt-0.5">{importResult.failedCount}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Skipped</p>
                  <p className="text-2xl font-bold text-amber-700 mt-0.5">{importResult.skippedCount}</p>
                </div>
              </div>

              {/* Log Filters Bar */}
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 border-b border-slate-100 pb-3">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Row Logs:</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(['ALL', 'SUCCESS', 'FAILED', 'SKIPPED'] as const).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setLogFilter(filter)}
                      className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${logFilter === filter
                        ? filter === 'SUCCESS' ? 'bg-emerald-600 text-white shadow-sm'
                          : filter === 'FAILED' ? 'bg-red-600 text-white shadow-sm'
                            : filter === 'SKIPPED' ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-slate-800 text-white shadow-sm'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                        }`}
                    >
                      {filter === 'ALL' ? `All (${importResult.rowLogs.length})`
                        : filter === 'SUCCESS' ? `Success (${importResult.successCount})`
                          : filter === 'FAILED' ? `Failed (${importResult.failedCount})`
                            : `Skipped (${importResult.skippedCount})`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row Logs Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 z-10">
                      <tr>
                        <th className="py-2.5 px-3 font-semibold text-slate-600 w-12 text-center">Row</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Ticket ID</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Idea Name</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Category</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Assignees</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Status</th>
                        <th className="py-2.5 px-3 font-semibold text-slate-600">Details / Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredLogs.map((log, i) => (
                        <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-2 px-3 text-slate-400 font-mono text-center font-medium">{log.rowNum}</td>
                          <td className="py-2 px-3 font-semibold text-slate-700 whitespace-nowrap">{log.ticketId}</td>
                          <td className="py-2 px-3 text-slate-800 max-w-[160px] truncate" title={log.ideaName}>{log.ideaName}</td>
                          <td className="py-2 px-3 text-slate-600 font-mono">{log.evaluationCategory || '-'}</td>
                          <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate" title={log.assigneeEmail}>{log.assigneeEmail || '-'}</td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            {log.status === 'SUCCESS' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
                                <CheckCircle2 className="w-3 h-3" /> SUCCESS
                              </span>
                            )}
                            {log.status === 'FAILED' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
                                <AlertCircle className="w-3 h-3" /> FAILED
                              </span>
                            )}
                            {log.status === 'SKIPPED' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
                                <Info className="w-3 h-3" /> SKIPPED
                              </span>
                            )}
                          </td>
                          <td className={`py-2 px-3 ${log.status === 'FAILED' ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                            {log.reason}
                          </td>
                        </tr>
                      ))}
                      {filteredLogs.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-6 text-center text-slate-400 italic">
                            No logs found for this filter tab.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          {!importResult ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 text-slate-700 font-medium text-xs rounded-xl hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUploadAndProcess}
                disabled={!file || isProcessing}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl transition-all shadow-md shadow-emerald-600/20 flex items-center space-x-2 cursor-pointer"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing Excel...</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-4 h-4" />
                    <span>Upload & Process Updates</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 border border-slate-300 text-slate-700 font-medium text-xs rounded-xl hover:bg-slate-100 transition-colors flex items-center space-x-1.5 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Upload Another File</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Done / Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
